import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { X, ChevronLeft, ChevronRight, ArrowRight, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default gallery images (fallback if no images in database)
const DEFAULT_IMAGES = [
  {
    id: "default-1",
    url: "https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/25yywsz1_1000015911.jpg",
    title: "Floral Entrance Arch",
    description: "Beautiful flower arch decoration for wedding events"
  },
  {
    id: "default-2",
    url: "https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/1s8h0y7f_1000015914.jpg",
    title: "Sweet Cart Display",
    description: "Vintage sweet cart with drapes and fairy lights"
  },
  {
    id: "default-3",
    url: "https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/k3fmts5h_1000015917.jpg",
    title: "Wedding Reception Setup",
    description: "Hall decorated with drapes, chair covers, and table settings"
  },
  {
    id: "default-4",
    url: "https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/waaf8owz_1000015920.jpg",
    title: "Evening Reception",
    description: "Beautiful evening setup with DJ booth and LOVE sign"
  },
  {
    id: "default-5",
    url: "https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/9x4wxxhj_1000015923.jpg",
    title: "Elegant Table Layout",
    description: "Spacious hall with elegant table arrangements"
  }
];

export default function GalleryPage() {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useSEO({
    title: "Photo Gallery | The Elizabethan Centre, Whaplode Drove",
    description: "Browse photos of The Elizabethan Centre - see our venue transformed for weddings, parties and events. View our hall with drapes, chair covers and elegant decorations.",
    canonical: "https://www.elizabethancentre.co.uk/gallery"
  });

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        if (response.data && response.data.length > 0) {
          // Format URLs for images from API
          const formattedImages = response.data.map(img => ({
            ...img,
            url: img.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img.url}` : img.url
          }));
          setGalleryImages(formattedImages);
        } else {
          // Use default images if no images in database
          setGalleryImages(DEFAULT_IMAGES);
        }
      } catch (error) {
        console.error("Error fetching gallery:", error);
        setGalleryImages(DEFAULT_IMAGES);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(galleryImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = useCallback(() => {
    const newIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  }, [currentIndex, galleryImages]);

  const goToNext = useCallback(() => {
    const newIndex = currentIndex === galleryImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  }, [currentIndex, galleryImages]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, goToPrevious, goToNext]);

  return (
    <div data-testid="gallery-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#F5F5F0]" data-testid="gallery-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#14532D]/10 text-[#14532D] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              <Camera className="w-4 h-4 inline mr-2" />
              Photo Gallery
            </span>
            <h1 className="heading-xl mb-4">See Our Venue</h1>
            <p className="text-xl text-[#292524]">
              Browse photos from events held at Whaplode Drove Social Club Hall. 
              See how our versatile venue can be transformed for your special occasion.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding" data-testid="gallery-grid">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, index) => (
                <div
                  key={image.id}
                  className="group relative aspect-[4/3] overflow-hidden rounded-xl cursor-pointer"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <img
                    src={image.url}
                    alt={image.title || "Gallery image"}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-white font-semibold font-serif text-lg mb-1">
                        {image.title || "Gallery Photo"}
                      </h3>
                      {image.description && (
                        <p className="text-white/80 text-sm">
                          {image.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="absolute inset-0 border-4 border-transparent group-hover:border-[#14532D] rounded-xl transition-colors duration-300" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="gallery-info">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg mb-6">Transform Our Hall for Your Event</h2>
            <p className="text-body mb-8">
              Our hall can be decorated to suit any occasion. From elegant weddings with drapes and 
              chair covers to corporate events and birthday parties. We offer optional extras 
              including drapes, chair covers, table cloths, and AV equipment.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book">
                <Button className="btn-primary">
                  Book the Hall
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="btn-outline">
                  View Facilities
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-50"
            onClick={closeLightbox}
            aria-label="Close lightbox"
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>

          {/* Previous button */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 z-50"
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={48} />
          </button>

          {/* Next button */}
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 z-50"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight size={48} />
          </button>

          {/* Image */}
          <div 
            className="max-w-5xl max-h-[85vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <h3 className="text-white font-semibold font-serif text-xl mb-1">
                {selectedImage.title}
              </h3>
              <p className="text-white/70">
                {selectedImage.description}
              </p>
              <p className="text-white/50 text-sm mt-2">
                {currentIndex + 1} / {galleryImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
